import React, { CSSProperties, ReactNode } from "react";
import {
  DialogContextProvider,
  FeatureFlagContextProvider,
  LocalizedTextContextProvider,
  NotificationsContextProvider,
  PortalSettingsContextProvider,
  ProgressIndicatorOverlayContextProvider,
  ScreenSizeContextProvider,
  TenantContextProvider,
  UserContextProvider,
} from "../../contexts";
import {
  AppSettings,
  FeatureFlagProviderName,
  FeatureFlagProvidersMap,
  TextTemplateKey,
  TextTemplatesByLanguage,
  User,
} from "../types";
import { AppLayout } from "./app-layout";

export interface AppProps<T extends TextTemplateKey, C extends object, F extends FeatureFlagProviderName> {
  appSettings?: AppSettings<T, C, F>;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

const defaultUser: User = {
  isSignedIn: false,
  permissions: [],
  roles: [],
};

const defaultFeatureFlagProvidersMap: FeatureFlagProvidersMap<string> = {};

const defaultTextTemplatesByLanguage: TextTemplatesByLanguage<string> = {
  en_US: {},
};

/**
 * The Blueprint `App` component provides a convenient abstraction that provides common application level functionality
 * that can be accessed from anywhere within the descendent component-tree wrapped within it. Internally, it essentially
 * uses the `AppContext` and maintains the associated state and callback functions to be passed as the context value
 * to it.
 *
 * Internally, the `AppContext` simply composes all individual Context Providers. Additionally the `App` component and
 * `AppContext` provide an integrated API for rendering the global header and breadcrumbs too, something that is not
 * covered by the individual Context Providers (by design). This common functionality can be accessed via the single
 * `useAppContext` custom hook.
 *
 * > **Note:** The `App` component, `AppContext` and the `useAppContext` hook are deprecated.
 * > Do not use them for any new build. Instead use the individual Context Provider components and their
 * companion hooks.
 *
 * @deprecated
 */
export const App = <
  T extends TextTemplateKey = TextTemplateKey,
  C extends object = object,
  F extends FeatureFlagProviderName = FeatureFlagProviderName
>(
  props: AppProps<T, C, F>
) => {
  return (
    <TenantContextProvider tenant={props.appSettings?.tenant}>
      <UserContextProvider user={props.appSettings?.user || defaultUser}>
        <FeatureFlagContextProvider
          providers={props.appSettings?.featureFlagProviders || defaultFeatureFlagProvidersMap}
        >
          <LocalizedTextContextProvider
            language={props.appSettings?.language || "en_US"}
            textTemplatesByLanguage={props.appSettings?.textTemplatesByLanguage || defaultTextTemplatesByLanguage}
          >
            <ScreenSizeContextProvider
              breakpoints={props.appSettings?.breakpoints}
              desktopBreakpoint={props.appSettings?.desktopBreakpoint}
              defaultScreenSize={props.appSettings?.defaultScreenSize}
            >
              <PortalSettingsContextProvider disabled={props.appSettings?.disablePortal || false}>
                <ProgressIndicatorOverlayContextProvider>
                  <DialogContextProvider>
                    <NotificationsContextProvider
                      autoCloseDelay={props.appSettings?.notificationsAutoCloseDelay}
                      notificationsWidth={props.appSettings?.notificationsWidth}
                      notificationsMinWidth={props.appSettings?.notificationsMinWidth}
                      notificationsMaxWidth={props.appSettings?.notificationsMaxWidth}
                    >
                      <AppLayout appProps={props}>{props.children}</AppLayout>
                    </NotificationsContextProvider>
                  </DialogContextProvider>
                </ProgressIndicatorOverlayContextProvider>
              </PortalSettingsContextProvider>
            </ScreenSizeContextProvider>
          </LocalizedTextContextProvider>
        </FeatureFlagContextProvider>
      </UserContextProvider>
    </TenantContextProvider>
  );
};
